/**
 * questAdapter — les quêtes de l'élève (`currentUser.skill_sets`, posées par `getSkillSets`), lues une
 * seule fois pour l'accueil (la plus avancée, et le rang), « Mon compte », « Mes quêtes », le détail
 * d'une quête et la quête de Tina dans l'accusé d'un achat.
 *
 * Une quête se lit comme l'ancien `SkillItem.js` la lisait :
 *  - l'étape montrée est `steps[current_step]`, sur `steps.length - 1` étapes (« 3/10 ») — `steps[0]`
 *    est le point de départ, pas une étape ;
 *  - son total est `skills[skill_id].levels_totals[skill_level]` (`staticData.skills`), son compte
 *    `current_count` ;
 *  - sa phrase est `description` (`description_sng` pour un total de 1), `[count]` remplacé par le total ;
 *  - elle rapporte `petals_gained`, et un titre quand `title_gained` change à cette étape ;
 *  - « Aujourd'hui : N minutes » pour la compétence 8, si le compte porte la durée du jour.
 *
 * Le détail (`MyProgressScreen.js`) : les étapes 1 à N (`steps.slice(1)`), faites avant l'étape en
 * cours, à venir après.
 *
 * Les formes `Api*` sont lâches : les quêtes viennent d'un réducteur en JS, tout est facultatif.
 * Une quête menée au bout, ou qu'on ne sait pas lire (champion inconnu, compétence absente), ne se
 * montre pas.
 */
import { championSlugsToNames } from "../../../../../data/constants/championsConstants"
import { type UIQuest, type UIQuestDetail, type UIQuestStep } from "../../../ui/types/studentGame"

export type ApiGameSkillSetStep = {
    skill_id?: number | string
    skill_level?: number | string
    title_gained?: string | null
    petals_gained?: number | null
}

export type ApiGameSkillSet = {
    id?: number
    name?: string
    champion?: string
    current_step?: number
    current_count?: number
    /** Booléen du pivot `user_skill_sets`, servi en entier (`0`/`1`) : lu comme l'ancien code, faux = non lue. */
    is_last_evolution_read?: boolean | number | null
    steps?: ApiGameSkillSetStep[] | null
}

/** Une compétence des données statiques (`staticData.skills`). */
export type ApiGameSkill = {
    id?: number | string
    description?: string | null
    description_sng?: string | null
    levels_totals?: Record<string, number> | number[] | null
}

/** Ce que la lecture d'une quête prend du compte : la durée d'activité du jour. */
export type ApiGameStatistics = { today?: { activities?: { duration?: number } } } | null

const CHAMPIONS = ["sam", "zoe", "tina", "abou"] as const
type Champion = (typeof CHAMPIONS)[number]
const isChampion = (slug: string | undefined): slug is Champion => CHAMPIONS.includes(slug as Champion)

/** « de Zoé », « d'Abou » — `formatFrenchDe` de `SkillSetTitleButton.js`. */
export function formatFrenchDe(word: string | null | undefined): string {
    const value = word ?? ""
    return ["a", "e", "i", "o", "u", "y"].includes(value.substring(0, 1).toLowerCase()) ? `d'${value}` : `de ${value}`
}

/**
 * La quête la plus avancée (accueil, « Mon compte ») — port littéral de `getMostAdvancedSkillSet`
 * (`MyAccountScreen.js`), y compris son asymétrie : le candidat compte `current_step - 1`, le meilleur
 * `current_step`.
 */
export function mostAdvancedSkillSet<T extends { current_step?: number; steps?: unknown[] | null }>(
    skillSets: T[] | null | undefined
): T | null {
    if (!skillSets || skillSets.length === 0) return null
    return skillSets.reduce<T | null>((mostAdvanced, current) => {
        const steps = current?.steps ?? []
        if (steps.length === 0) return mostAdvanced
        const currentRatio = ((current.current_step ?? 0) - 1) / steps.length
        const mostAdvancedRatio =
            mostAdvanced != null ? (mostAdvanced.current_step ?? 0) / (mostAdvanced.steps?.length ?? 1) : 0
        return currentRatio >= mostAdvancedRatio ? current : mostAdvanced
    }, null)
}

/** « 1ᵉʳ », « 12ᵉ » — que le legacy affichait en nombre nu. Pas de rang servi (ou zéro) : rien. */
export function rankLabel(rank: number | null | undefined): string | null {
    if (rank == null || rank <= 0) return null
    return rank === 1 ? "1ᵉʳ" : `${rank}ᵉ`
}

/** La compétence dont la quête dit le temps du jour (`SkillItem.js`, `skill?.id === 8`). */
const TODAY_DURATION_SKILL_ID = 8

type StepReading = {
    description: string
    /** `null` : la compétence de l'étape n'est pas dans les données statiques. */
    total: number | null
    skillId: string | null
    petals: number | null
    titleGained: string | null
}

/** Une étape d'une quête, lue comme `SkillItem.js` la lit. */
function readStep(
    steps: ApiGameSkillSetStep[],
    index: number,
    skills: ApiGameSkill[] | null | undefined
): StepReading | null {
    const step = steps[index]
    if (step == null) return null
    const skill = (skills ?? []).find((candidate) => String(candidate?.id) === String(step.skill_id))
    const levels = skill?.levels_totals as Record<string, number> | null | undefined
    const level = step.skill_level != null ? levels?.[String(step.skill_level)] : undefined
    const total = typeof level === "number" ? level : null
    const template = total === 1 ? skill?.description_sng : skill?.description
    const titleGained = step.title_gained !== steps[index - 1]?.title_gained ? (step.title_gained ?? null) : null
    return {
        description: total != null ? (template ?? "").replace("[count]", String(total)) : "",
        total,
        skillId: skill?.id != null ? String(skill.id) : null,
        petals: step.petals_gained ?? null,
        titleGained: titleGained !== "" ? titleGained : null,
    }
}

export type ToUIQuestOptions = {
    /**
     * Lire la quête comme `SkillItem.js` AVEC son animation (`withAnimation`, l'accusé d'un achat) :
     * tant que l'élève n'a pas vu sa dernière évolution (`is_last_evolution_read` faux) et que
     * l'étape en cours est vierge, on montre l'étape tout juste finie, pleine.
     */
    showLastEvolution?: boolean
}

export function toUIQuest(
    skillSet: ApiGameSkillSet | null | undefined,
    skills: ApiGameSkill[] | null | undefined,
    statistics?: ApiGameStatistics,
    options: ToUIQuestOptions = {}
): UIQuest | null {
    if (skillSet?.id == null || !isChampion(skillSet.champion)) return null
    const steps = skillSet.steps ?? []
    const currentStep = skillSet.current_step ?? 0
    const currentCount = skillSet.current_count ?? 0

    const justFinished =
        options.showLastEvolution === true &&
        !skillSet.is_last_evolution_read &&
        currentCount === 0 &&
        currentStep - 1 >= 1
    const shown = justFinished ? currentStep - 1 : currentStep

    const reading = readStep(steps, shown, skills)
    if (reading == null || reading.total == null) return null
    const total = reading.total

    // `SkillItem.js` : une étape passée est pleine, l'étape en cours compte `current_count`.
    const count = shown < currentStep ? total : Math.min(currentCount, total)

    const todaySeconds = statistics?.today?.activities?.duration
    const todayLabel =
        reading.skillId === String(TODAY_DURATION_SKILL_ID) && count < total && Number.isFinite(todaySeconds)
            ? `Aujourd'hui : ${Math.floor((todaySeconds as number) / 60)} minutes`
            : null

    return {
        id: skillSet.id,
        label: `Quête ${formatFrenchDe(championSlugsToNames[skillSet.champion])}`,
        champion: skillSet.champion,
        step: shown,
        stepCount: steps.length - 1,
        description: reading.description,
        count,
        total,
        todayLabel,
        petals: reading.petals,
        titleGained: reading.titleGained,
        status: count >= total ? "done" : "current",
    }
}

/**
 * « Mes quêtes » : une carte par quête, dans l'ordre du serveur. `null` tant que les quêtes ou les
 * compétences ne sont pas là — l'ancien écran ne montrait rien sans les deux.
 */
export function toUIQuests(
    skillSets: ApiGameSkillSet[] | null | undefined,
    skills: ApiGameSkill[] | null | undefined,
    statistics?: ApiGameStatistics
): UIQuest[] | null {
    if (!skillSets || !skills) return null
    return skillSets
        .map((skillSet) => toUIQuest(skillSet, skills, statistics))
        .filter((quest): quest is UIQuest => quest != null)
}

/**
 * Le détail d'une quête — `MyProgressScreen.js` : l'étape en cours en grand, puis les étapes 1 à N,
 * faites (`stepNum < current_step`) ou à venir (`stepNum > current_step`). Ce qu'une étape rapporte
 * ne se dit que pour l'étape en cours et les suivantes : `<QuestStepRow>` le tait sur une étape faite.
 */
export function toUIQuestDetail(
    skillSet: ApiGameSkillSet | null | undefined,
    skills: ApiGameSkill[] | null | undefined,
    statistics?: ApiGameStatistics
): UIQuestDetail | null {
    const quest = toUIQuest(skillSet, skills, statistics)
    if (skillSet == null || quest == null) return null
    const steps = skillSet.steps ?? []
    const currentStep = skillSet.current_step ?? 0

    const doneSteps: UIQuestStep[] = []
    const lockedSteps: UIQuestStep[] = []
    for (let stepNum = 1; stepNum < steps.length; stepNum += 1) {
        if (stepNum === currentStep) continue
        const reading = readStep(steps, stepNum, skills)
        const row: UIQuestStep = {
            step: stepNum,
            description: reading?.description ?? "",
            status: stepNum < currentStep ? "done" : "locked",
            petals: reading?.petals ?? null,
            titleGained: reading?.titleGained ?? null,
        }
        if (stepNum < currentStep) doneSteps.push(row)
        else lockedSteps.push(row)
    }

    return { name: skillSet.name ?? "", quest, doneSteps, lockedSteps }
}

/** Ce que l'accueil lit du compte pour son jeu : les quêtes, la durée du jour, le rang. */
export type ApiGameAccount = {
    xp?: number
    rank?: number | null
    skill_sets?: ApiGameSkillSet[] | null
    statistics?: ApiGameStatistics
}

/** La quête de l'accueil : la plus avancée, à son étape. */
export function toHomeQuest(account: ApiGameAccount | null | undefined, skills: ApiGameSkill[] | null | undefined) {
    return toUIQuest(mostAdvancedSkillSet(account?.skill_sets), skills, account?.statistics)
}

/** Le rang de l'accueil (`User::getRankAttribute`). Sans XP, l'élève n'est pas encore classé : pas de ligne. */
export function toRankLabel(account: ApiGameAccount | null | undefined): string | null {
    if (!(account?.xp != null && account.xp > 0)) return null
    return rankLabel(account.rank)
}
